/*
 * Chat transcript persistence, scoped to the current tab session. The transcript and
 * conversationId live in sessionStorage so the panel survives in-app navigation
 * (react-router unmounts the route) but starts fresh in a new tab. Keeping the same
 * conversationId also preserves the server-side conversation memory across remounts.
 * The session is auth-scoped, not just tab-scoped: logout (handleNotLoggedIn) clears
 * it so a transcript never carries over into another user's session in the same tab.
 */

export interface ChatMessage {
  /** "note" is a chat-side notice (e.g. field updates the page rejected) — plain text, never from the server. */
  role: "user" | "assistant" | "error" | "note";
  text: string;
}

export interface ChatSession {
  conversationId: string;
  messages: ChatMessage[];
}

const STORAGE_KEY = "ai-chat-session";

function newConversationId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Older browsers and jsdom lack randomUUID; uniqueness per tab-session is all we need.
  return `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function freshSession(): ChatSession {
  return { conversationId: newConversationId(), messages: [] };
}

// Persistence is best-effort: parse errors, foreign shapes, and unavailable/full storage
// must never break the chat, so every storage access is guarded.
export function loadChatSession(): ChatSession {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (typeof parsed?.conversationId === "string" && Array.isArray(parsed?.messages)) {
        return { conversationId: parsed.conversationId, messages: parsed.messages };
      }
    }
  } catch {
    // fall through to a fresh session
  }
  return freshSession();
}

export function saveChatSession(session: ChatSession): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // best-effort only
  }
}

export function clearChatSession(): ChatSession {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort only
  }
  return freshSession();
}
